/**
 * web/src/components/access/AccessToolbar.tsx
 * Toolbar above the user roster table — search input + "Invite user"
 * affordance. The matrix-era event filter + sync-from-sheet button were
 * removed when the workflow-grants matrix was retired (grants were
 * advisory only and nothing read them downstream).
 *
 * Invite flow: opens a dialog that collects email + name + role and POSTs
 * to /api/admin/users/invite. No email is sent — we pre-create the User
 * row so Google SSO matches the invitee on first sign-in (OAuth callback
 * is whitelist-only, see src/api/auth.ts).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '../shared/Button';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Icon } from '../../lib/icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/dialog';
import { useInviteUser } from '../../hooks/mutations/useInviteUser';
import type { Role } from '../../api/types';

/** Public props for AccessToolbar. */
export interface AccessToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
}

/** Roles exposed in the invite dropdown — order matches UserList's row picker. */
const ROLE_OPTIONS: Array<{ value: Role; label: string }> = [
  { value: 'sales_ops', label: 'Sales ops' },
  { value: 'content', label: 'Content' },
  { value: 'operations', label: 'Operations' },
  { value: 'admin', label: 'Admin' },
];

/**
 * Renders the user-roster toolbar (search + invite). The Invite button opens
 * an InviteUserDialog; on success the new user appears in the roster via the
 * useInviteUser hook's cache update.
 */
export function AccessToolbar({ query, onQueryChange }: AccessToolbarProps) {
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <>
      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-shrink-0 w-[280px] max-w-full">
          <span
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint"
            aria-hidden="true"
          >
            <Icon name="search" size={13} />
          </span>
          <Input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search users…"
            aria-label="Search users"
            className="pl-8 pr-3"
          />
        </div>
        <div className="flex-1" />
        <Button
          variant="primary"
          size="sm"
          onClick={() => setInviteOpen(true)}
          title="Add a user so they can sign in with Google"
        >
          <Icon name="plus" size={11} />
          Invite user
        </Button>
      </div>
      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </>
  );
}

/* -------------------------------------------------------------------- */
/* Invite user dialog                                                     */
/* -------------------------------------------------------------------- */

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const invite = useInviteUser();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('sales_ops');

  const trimmedEmail = email.trim().toLowerCase();
  const trimmedName = name.trim();
  const canSubmit =
    trimmedEmail.length > 0 &&
    /\S+@\S+\.\S+/.test(trimmedEmail) &&
    trimmedName.length > 0 &&
    !invite.isPending;

  function reset() {
    setEmail('');
    setName('');
    setRole('sales_ops');
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    invite.mutate(
      { email: trimmedEmail, name: trimmedName, role },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (invite.isPending) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody>
            <p className="text-[12.5px] text-text-muted leading-[1.5] mb-4">
              Adds the user to the roster so they can sign in with Google SSO. No invite email is
              sent — share the dashboard URL directly with the invitee once they're added.
            </p>
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-text-subtle">Email</span>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@builtwithpixel.com"
                  autoFocus
                  required
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-text-subtle">Full name</span>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  required
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[12px] font-medium text-text-subtle">Role</span>
                <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="default"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={invite.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {invite.isPending ? 'Inviting…' : 'Add user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
